import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
// LA VESTE: la stessa delle pagine pubbliche d'iscrizione (Login.jsx,
// Enrol.jsx): il guscio e i testi da HomeShell.css (si importa il FOGLIO, non
// il componente — questa è una pagina PUBBLICA, senza barra utente), i campi e
// i pulsanti da AdminTools.css, il poco che è proprio dei moduli d'iscrizione
// da Enrolment.css. Nessuno stile nuovo.
import "../components/HomeShell.css";
import "../components/AdminTools.css";
import "./Enrolment.css";
import { api } from "../api/api";
import { emailBenFormata, messaggioDelServer } from "../utils/enrolDati";
import { LinkTermini } from "./enrolShared";
import { CHIAVE_EMAIL_ACCESSO } from "./Login";

/**
 * LA PORTA DEL GUEST — /clinic/:slug (cantiere CLINICS, Fetta C1, 15/9/2026).
 * Base di verità: DISEGNO v2 §4, §7.
 *
 * Ci arriva, dal link distribuito dall'admin, un tutore ESTERNO al club: legge
 * a che cosa si sta iscrivendo (GET /clinic-public/:slug), iscrive un minore
 * coi dati minimi e il consenso (POST /clinic-public/:slug/enrol), e riceve per
 * email il link d'accesso. Aprendolo entra da /finish-login, come ogni socio, e
 * il cancello lo porta su /home/guest.
 *
 * ⚠️ ROTTA NUDA, sul calco di /finish-login e NON dietro PublicRoute: quel
 * `<Navigate to="/home">` riscriverebbe l'indirizzo e perderebbe lo slug a chi
 * ha una sessione viva. Chi è già socio e apre il link vede il modulo; se manda
 * la propria email il server risponde, neutro, che quell'indirizzo è già
 * registrato — l'iscrizione del SOCIO dalla propria area è la Fetta C2.
 *
 * ⚠️ IL CONTROLLO CHE CONTA È DEL SERVER. La pagina mostra il modulo solo se
 * l'evento risponde (cioè è OPEN), ma è la rotta di iscrizione a rileggere lo
 * stato a ogni invio: un evento chiuso fra la lettura e l'invio è un 409, non
 * un'iscrizione. Qui si evita soltanto di far partire ciò che si sa già
 * rifiutato, e si dice perché.
 *
 * ⚠️ ELENCO CHIUSO in partenza, come in EnrolFamily: partono i soli campi che
 * il server legge. Nessun `role`, nessuno `status`: chi nasce da qui è GUEST
 * per decisione del server, non del modulo.
 *
 * ⚠️ DUE RAMI dalla Fetta G1 (24/9/2026). In cima al modulo «Who are you
 * enrolling?»: «My child» — il modulo di C1, invariato — oppure «Myself», un
 * ADULTO (18 anni o più) che si iscrive da sé, senza sezione del tutore e con
 * «Joining as: Player / Coach». Nessuna delle due è preselezionata: i campi
 * compaiono dopo la scelta. Il corpo porta `enrolling` ("child" / "self"); il
 * server tratta un corpo senza `enrolling` come «My child», e decide lui
 * l'età (18 o più per «Myself», meno di 18 per «My child»).
 *
 * ⚠️ SOLO INGLESE (CLAUDE.md §3.7). Le lingue d'aiuto dei moduli d'iscrizione
 * (enrolLingue/enrolAiuto) non sono cablate qui: è una scelta di perimetro di
 * C1, non un'esclusione — si aggiungono quando il committente lo chiede.
 */

// Il modulo, vuoto. `consent` è la spunta dei termini del club: la stessa
// formula (LinkTermini) e la stessa lettura (`valoreVero` sul server) degli
// altri moduli.
const MODULO_VUOTO = {
  childFirstName: "",
  childLastName: "",
  childDob: "",
  guardianFirstName: "",
  guardianLastName: "",
  guardianEmail: "",
  consent: false,
};

// Il modulo di «Myself» (G1): la persona stessa, senza tutore. Tenuto a parte
// da quello del bambino: chi torna sulla scelta ritrova ciascuno com'era.
const MODULO_ADULTO_VUOTO = {
  firstName: "",
  lastName: "",
  dob: "",
  email: "",
  joiningAs: "",
  consent: false,
};

// «Joining as»: i due valori che il server accetta, e come si leggono.
const JOINING_AS = [
  { valore: "PLAYER", etichetta: "Player" },
  { valore: "COACH", etichetta: "Coach" },
];

// Il primo campo fuori regola, in parole. È un vaglio di CORTESIA: il metro
// vero (data esistente, minore età, casella già usata, evento chiuso) è del
// server, e i suoi «no» parlanti si mostrano tali e quali.
function primoErroreDelModulo(form) {
  if (!form.childFirstName.trim() || !form.childLastName.trim()) {
    return "Please enter the child's first and last name.";
  }
  if (!form.childDob) {
    return "Please enter the child's date of birth.";
  }
  if (!form.guardianFirstName.trim() || !form.guardianLastName.trim()) {
    return "Please enter your first and last name.";
  }
  if (!emailBenFormata(form.guardianEmail)) {
    return "Please enter a valid email address.";
  }
  if (!form.consent) {
    return "Please accept the club's terms and conditions to continue.";
  }
  return "";
}

// Lo stesso vaglio di cortesia per «Myself»: il metro vero (data, 18 anni,
// casella già usata) resta del server.
function primoErroreDelModuloAdulto(form) {
  if (!form.firstName.trim() || !form.lastName.trim()) {
    return "Please enter your first and last name.";
  }
  if (!form.dob) {
    return "Please enter your date of birth.";
  }
  if (!emailBenFormata(form.email)) {
    return "Please enter a valid email address.";
  }
  if (!JOINING_AS.some((voce) => voce.valore === form.joiningAs)) {
    return "Please choose whether you are joining as a player or a coach.";
  }
  if (!form.consent) {
    return "Please accept the club's terms and conditions to continue.";
  }
  return "";
}

// Che cosa dire quando l'invio non riesce: il rifiuto del server con le sue
// parole, altrimenti il guasto — e nel guasto non si dà la colpa a ciò che la
// persona ha scritto (stessa distinzione di enrolDati.messaggioDiErrore).
function spiegazioneDiUnErrore(err) {
  const dalServer = messaggioDelServer(err);
  if (dalServer) return dalServer;

  return err && err.status
    ? "The club server refused the enrolment. Nothing was saved."
    : "The club server could not be reached. Nothing was saved. Please try again.";
}

export default function ClinicGuestEnrol() {
  const { slug } = useParams();

  const [evento, setEvento] = useState(null);
  const [caricamento, setCaricamento] = useState(true);
  const [erroreCaricamento, setErroreCaricamento] = useState("");

  // Chi si iscrive (G1): null finché non si sceglie, poi "child" o "self".
  const [chi, setChi] = useState(null);
  const [form, setForm] = useState(MODULO_VUOTO);
  const [formAdulto, setFormAdulto] = useState(MODULO_ADULTO_VUOTO);
  const [errore, setErrore] = useState("");
  const [saving, setSaving] = useState(false);
  const [esito, setEsito] = useState(null);

  useEffect(() => {
    let vivo = true;

    (async () => {
      setCaricamento(true);
      setErroreCaricamento("");

      try {
        const risposta = await api.get(`/clinic-public/${encodeURIComponent(slug || "")}`);
        if (vivo) setEvento(risposta || null);
      } catch (err) {
        if (!vivo) return;
        // Un 404 è «questo link non porta a nulla»: sconosciuto o chiuso, per
        // chi sta fuori è la stessa cosa (e il server non distingue). Il resto
        // è un guasto, e si dice come tale.
        setErroreCaricamento(
          err && err.status === 404
            ? "This clinic link is not valid or no longer active. Please check with the club."
            : "The club server could not be reached. Please try again later."
        );
      } finally {
        if (vivo) setCaricamento(false);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [slug]);

  const aggiorna = (campo, valore) =>
    setForm((prev) => ({ ...prev, [campo]: valore }));

  const aggiornaAdulto = (campo, valore) =>
    setFormAdulto((prev) => ({ ...prev, [campo]: valore }));

  const scegli = (valore) => {
    if (saving) return;
    setChi(valore);
    setErrore("");
  };

  const invia = async (e) => {
    e.preventDefault();
    if (saving) return;

    const daSe = chi === "self";
    const problema = daSe
      ? primoErroreDelModuloAdulto(formAdulto)
      : primoErroreDelModulo(form);
    if (problema) {
      setErrore(problema);
      return;
    }

    setErrore("");
    setSaving(true);

    try {
      // ⚠️ ELENCO CHIUSO: i soli campi che il server legge per il ramo
      // scelto, più `enrolling` (G1).
      const corpo = daSe
        ? {
            enrolling: "self",
            firstName: formAdulto.firstName.trim(),
            lastName: formAdulto.lastName.trim(),
            dob: formAdulto.dob,
            email: formAdulto.email.trim(),
            joiningAs: formAdulto.joiningAs,
            consent: formAdulto.consent,
          }
        : {
            enrolling: "child",
            childFirstName: form.childFirstName.trim(),
            childLastName: form.childLastName.trim(),
            childDob: form.childDob,
            guardianFirstName: form.guardianFirstName.trim(),
            guardianLastName: form.guardianLastName.trim(),
            guardianEmail: form.guardianEmail.trim(),
            consent: form.consent,
          };
      const emailDiAccesso = daSe ? corpo.email : corpo.guardianEmail;

      const risposta = await api.post(
        `/clinic-public/${encodeURIComponent(slug || "")}/enrol`,
        corpo
      );

      // L'indirizzo si parcheggia dove FinishLogin.jsx lo cerca al ritorno
      // dal link (stessa chiave di Login.jsx): così il tutore che apre il link
      // non deve ribattere l'email. Se il browser lo vieta, FinishLogin la
      // chiederà a mano — non è un guaio.
      try {
        window.localStorage.setItem(
          CHIAVE_EMAIL_ACCESSO,
          emailDiAccesso.toLowerCase()
        );
      } catch (_) {
        /* localStorage non disponibile: si prosegue lo stesso. */
      }

      setEsito({
        emailSent: Boolean(risposta && risposta.emailSent),
        self: daSe,
        child: daSe ? "" : `${corpo.childFirstName} ${corpo.childLastName}`,
        email: emailDiAccesso,
      });
      setForm(MODULO_VUOTO);
      setFormAdulto(MODULO_ADULTO_VUOTO);
    } catch (err) {
      setErrore(spiegazioneDiUnErrore(err));
    } finally {
      setSaving(false);
    }
  };

  const testa = (
    <header className="qpsc-en-head">
      <img src="/images/logo.png" alt="QPSC Logo" className="qpsc-en-logo" />
      <h2 className="qpsc-page-title">Clinic enrolment</h2>
    </header>
  );

  // ATTERRAGGIO DOPO IL 201. A differenza dei moduli d'iscrizione dei soci,
  // qui NON c'è un'attesa di approvazione: il GUEST è già dentro, e ciò che
  // gli manca è solo il link d'accesso, che il server ha spedito (o no: se la
  // posta del club ha taciuto, la strada è /login con la stessa casella, e si
  // dice).
  if (esito) {
    return (
      <div className="qpsc-page">
        <main className="qpsc-column">
          {testa}

          {/* «Myself» (G1): la frase è rivolta alla persona iscritta. */}
          {esito.self ? (
            <p className="qpsc-en-msg qpsc-en-msg--ok">
              Thank you — you are enrolled
              {evento && evento.title ? ` in "${evento.title}"` : ""}.
            </p>
          ) : (
            <p className="qpsc-en-msg qpsc-en-msg--ok">
              Thank you — {esito.child} is enrolled
              {evento && evento.title ? ` in "${evento.title}"` : ""}.
            </p>
          )}

          {esito.emailSent ? (
            <p className="qpsc-body-text">
              We've sent a sign-in link to <strong>{esito.email}</strong>. Open it
              to enter the QPSC app: no password is needed. If it doesn't arrive
              within a few minutes, check your spam folder or request a new link
              from the sign-in page using the same email address.
            </p>
          ) : (
            <p className="qpsc-body-text">
              Your enrolment has been recorded. To enter the QPSC app, go to the{" "}
              <a className="qpsc-en-link" href="/login">
                sign-in page
              </a>{" "}
              and request a sign-in link with <strong>{esito.email}</strong>: no
              password is needed.
            </p>
          )}

          {!esito.self && (
            <p className="qpsc-note">
              Children do not sign in themselves: you use the app on their behalf.
            </p>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="qpsc-page">
      <main className="qpsc-column">
        {testa}

        {caricamento && <p className="qpsc-body-text">Loading…</p>}

        {!caricamento && erroreCaricamento && (
          <p className="qpsc-en-msg qpsc-en-msg--error">{erroreCaricamento}</p>
        )}

        {!caricamento && evento && (
          <>
            {/* A CHE COSA ci si iscrive: nome e descrizione. Le date
                dell'evento non si mostrano più (cantiere CLINICS, Fetta 3,
                23/9): le date vere stanno nelle sessioni, giorni e orari in
                parole nella descrizione. Il server manda ancora `dates`, ma
                la pagina non lo legge. */}
            <section className="qpsc-en-section">
              <h3 className="qpsc-en-section__title">{evento.title}</h3>
              {evento.description && (
                <p className="qpsc-body-text">{evento.description}</p>
              )}
            </section>

            {/* CHI SI ISCRIVE (G1): il gruppo-scelta §6, nessuna opzione
                preselezionata. I campi compaiono dopo la scelta. */}
            <div className="qpsc-choice-group">
              <span className="qpsc-choice-group__label">
                Who are you enrolling?
              </span>
              <div className="qpsc-choice-group__options">
                <button
                  type="button"
                  className={
                    "qpsc-btn-dark" +
                    (chi === "child" ? " qpsc-choice-option--selected" : "")
                  }
                  aria-pressed={chi === "child"}
                  onClick={() => scegli("child")}
                  disabled={saving}
                >
                  My child (under 18)
                </button>
                <button
                  type="button"
                  className={
                    "qpsc-btn-dark" +
                    (chi === "self" ? " qpsc-choice-option--selected" : "")
                  }
                  aria-pressed={chi === "self"}
                  onClick={() => scegli("self")}
                  disabled={saving}
                >
                  Myself (18 or over)
                </button>
              </div>
            </div>

            {chi === "child" && (
              <form className="qpsc-en-form" onSubmit={invia} noValidate>
                <section className="qpsc-en-section">
                  <h3 className="qpsc-en-section__title">Child</h3>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">First name</span>
                    <input
                      className="qpsc-input"
                      name="childFirstName"
                      value={form.childFirstName}
                      onChange={(e) => aggiorna("childFirstName", e.target.value)}
                      maxLength={80}
                      autoComplete="off"
                      disabled={saving}
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">Last name</span>
                    <input
                      className="qpsc-input"
                      name="childLastName"
                      value={form.childLastName}
                      onChange={(e) => aggiorna("childLastName", e.target.value)}
                      maxLength={80}
                      autoComplete="off"
                      disabled={saving}
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">Date of birth</span>
                    <input
                      className="qpsc-input"
                      type="date"
                      name="childDob"
                      value={form.childDob}
                      onChange={(e) => aggiorna("childDob", e.target.value)}
                      disabled={saving}
                    />
                  </label>
                </section>

                <section className="qpsc-en-section">
                  <h3 className="qpsc-en-section__title">Parent / guardian</h3>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">First name</span>
                    <input
                      className="qpsc-input"
                      name="guardianFirstName"
                      value={form.guardianFirstName}
                      onChange={(e) => aggiorna("guardianFirstName", e.target.value)}
                      maxLength={80}
                      autoComplete="given-name"
                      disabled={saving}
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">Last name</span>
                    <input
                      className="qpsc-input"
                      name="guardianLastName"
                      value={form.guardianLastName}
                      onChange={(e) => aggiorna("guardianLastName", e.target.value)}
                      maxLength={80}
                      autoComplete="family-name"
                      disabled={saving}
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">Email</span>
                    <input
                      className="qpsc-input"
                      type="email"
                      name="guardianEmail"
                      value={form.guardianEmail}
                      onChange={(e) => aggiorna("guardianEmail", e.target.value)}
                      autoComplete="email"
                      disabled={saving}
                    />
                  </label>

                  <p className="qpsc-note">
                    Your sign-in link will be sent to this address. No password is
                    needed to use the QPSC app.
                  </p>
                </section>

                {/* IL CONSENSO: la formula legale del club, la stessa di ogni
                    modulo d'iscrizione, in un solo esemplare (enrolShared). */}
                <label className="qpsc-check">
                  <input
                    className="qpsc-check__input"
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={(e) => aggiorna("consent", e.target.checked)}
                    disabled={saving}
                  />
                  <span className="qpsc-check__box" aria-hidden="true" />
                  <LinkTermini />
                </label>

                {errore && (
                  <p className="qpsc-en-msg qpsc-en-msg--error" role="alert">
                    {errore}
                  </p>
                )}

                <button type="submit" className="qpsc-tool-btn" disabled={saving}>
                  {saving ? "Enrolling…" : "Enrol"}
                </button>
              </form>
            )}

            {/* «MYSELF» (G1): la persona stessa — niente sezione del tutore,
                e «Joining as». Stessa veste, stessa spunta dei termini: è
                la formula che la PWA fa accettare all'adulto che si iscrive
                da sé su /enrol (LinkTermini, un solo esemplare). */}
            {chi === "self" && (
              <form className="qpsc-en-form" onSubmit={invia} noValidate>
                <section className="qpsc-en-section">
                  <h3 className="qpsc-en-section__title">Your details</h3>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">First name</span>
                    <input
                      className="qpsc-input"
                      name="firstName"
                      value={formAdulto.firstName}
                      onChange={(e) => aggiornaAdulto("firstName", e.target.value)}
                      maxLength={80}
                      autoComplete="given-name"
                      disabled={saving}
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">Last name</span>
                    <input
                      className="qpsc-input"
                      name="lastName"
                      value={formAdulto.lastName}
                      onChange={(e) => aggiornaAdulto("lastName", e.target.value)}
                      maxLength={80}
                      autoComplete="family-name"
                      disabled={saving}
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">Date of birth</span>
                    <input
                      className="qpsc-input"
                      type="date"
                      name="dob"
                      value={formAdulto.dob}
                      onChange={(e) => aggiornaAdulto("dob", e.target.value)}
                      disabled={saving}
                    />
                  </label>

                  <label className="qpsc-field">
                    <span className="qpsc-field__label">Email</span>
                    <input
                      className="qpsc-input"
                      type="email"
                      name="email"
                      value={formAdulto.email}
                      onChange={(e) => aggiornaAdulto("email", e.target.value)}
                      autoComplete="email"
                      disabled={saving}
                    />
                  </label>

                  <p className="qpsc-note">
                    Your sign-in link will be sent to this address. No password is
                    needed to use the QPSC app.
                  </p>
                </section>

                <div className="qpsc-choice-group">
                  <span className="qpsc-choice-group__label">Joining as</span>
                  <div className="qpsc-choice-group__options">
                    {JOINING_AS.map((voce) => (
                      <button
                        key={voce.valore}
                        type="button"
                        className={
                          "qpsc-btn-dark" +
                          (formAdulto.joiningAs === voce.valore
                            ? " qpsc-choice-option--selected"
                            : "")
                        }
                        aria-pressed={formAdulto.joiningAs === voce.valore}
                        onClick={() => aggiornaAdulto("joiningAs", voce.valore)}
                        disabled={saving}
                      >
                        {voce.etichetta}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="qpsc-check">
                  <input
                    className="qpsc-check__input"
                    type="checkbox"
                    name="consent"
                    checked={formAdulto.consent}
                    onChange={(e) => aggiornaAdulto("consent", e.target.checked)}
                    disabled={saving}
                  />
                  <span className="qpsc-check__box" aria-hidden="true" />
                  <LinkTermini />
                </label>

                {errore && (
                  <p className="qpsc-en-msg qpsc-en-msg--error" role="alert">
                    {errore}
                  </p>
                )}

                <button type="submit" className="qpsc-tool-btn" disabled={saving}>
                  {saving ? "Enrolling…" : "Enrol"}
                </button>
              </form>
            )}
          </>
        )}
      </main>
    </div>
  );
}
